import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { scratchService, campaignService } from '../services/api.service';
import LoadingSpinner from '../components/LoadingSpinner';
import MyPrizesModal from '../components/MyPrizesModal';
import ProfileModal from '../components/ProfileModal';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';

const ButtonBase = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  border: 1px solid rgba(255, 215, 0, 0.2);
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const PrizesHistory = () => {
  const [prizes, setPrizes] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [selectedPrize, setSelectedPrize] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [prizesResponse, campaignsResponse] = await Promise.all([
          scratchService.getMyPrizes(),
          campaignService.getActiveCampaigns()
        ]);

        if (prizesResponse.success) {
          setPrizes(prizesResponse.prizes);
        }
        if (campaignsResponse.success) {
          setCampaigns(campaignsResponse.campaigns);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error cargando datos:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: '¿Cerrar sesión?',
      text: '¿Estás seguro que quieres salir?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#7c3aed',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error);
      }
    }
  };

  const handleWhatsAppShare = (prize) => {
    console.log('User data:', user?.userData);
    
    let phoneNumber = user?.userData?.phone || '';
    phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    console.log('Phone number after cleanup:', phoneNumber);

    if (!phoneNumber) {
      toast.error('No se encontró un número de teléfono válido');
      return;
    }

    let formattedPhone = phoneNumber;
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = formattedPhone.startsWith('34') ? formattedPhone : `34${formattedPhone}`;
    }
    formattedPhone = formattedPhone.replace('+', '');
    
    const message = encodeURIComponent(
      `🎉 ¡Has ganado un premio!\n\n` +
      `Premio: ${prize.name}\n` +
      `Código: ${prize.code}\n\n` +
      `Instrucciones de canjeo:\n${prize.campaign?.redeemInstructions || 'Contacta con el establecimiento'}`
    );

    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${message}`;
    const newWindow = window.open(whatsappUrl, '_blank');
    
    if (!newWindow) {
      toast.error('El navegador bloqueó la apertura de WhatsApp. Por favor, permite las ventanas emergentes.');
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Container>
      <GradientTopBar>
        <HeaderTitle>
          <MainTitle>El Rasca y Gana</MainTitle>
          <SubTitle>de Pamplonews</SubTitle>
        </HeaderTitle>
        <UserMenuContainer>
          <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <UserAvatar>
              {user?.email?.charAt(0).toUpperCase() || '👤'}
            </UserAvatar>
          </MenuButton>
          {isMenuOpen && (
            <MenuDropdown>
              <MenuItem onClick={() => {
                setIsMenuOpen(false);
                setIsProfileModalOpen(true);
              }}>
                <MenuIcon>👤</MenuIcon>
                <span>Mi Perfil</span>
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={() => {
                setIsMenuOpen(false);
                handleLogout();
              }}>
                <MenuIcon>↪</MenuIcon>
                <span>Cerrar Sesión</span>
              </MenuItem>
            </MenuDropdown>
          )}
        </UserMenuContainer>
      </GradientTopBar>

      <MainContent>
        <SectionContainer>
          <SectionTitle>Campañas Activas</SectionTitle>
          <List>
            {loading ? (
              <LoadingMessage>Cargando campañas...</LoadingMessage>
            ) : campaigns.length > 0 ? (
              campaigns.map((campaign) => (
                <CampaignCard key={campaign._id} onClick={() => navigate(`/campaign/${campaign.slug}`)}>
                  <CampaignInfo>
                    <CampaignTitle>{campaign.name}</CampaignTitle>
                    <PrizesCount>
                      {campaign.prizes.reduce((sum, prize) => sum + prize.quantity, 0)} premios
                    </PrizesCount>
                  </CampaignInfo>
                  <PlayButton>Participar</PlayButton>
                </CampaignCard>
              ))
            ) : (
              <EmptyMessage>No hay campañas activas en este momento</EmptyMessage>
            )}
          </List>
        </SectionContainer>

        <SectionContainer>
          <SectionTitle>Mis Premios</SectionTitle>
          <List>
            {loading ? (
              <LoadingMessage>Cargando premios...</LoadingMessage>
            ) : prizes.length > 0 ? (
              prizes.map((prize, index) => (
                <PrizeCard key={index}>
                  <PrizeInfo>
                    <PrizeName>{prize.name}</PrizeName>
                    <CampaignName>{prize.campaignName}</CampaignName>
                    <PrizeCode>Código: {prize.code}</PrizeCode>
                    <PrizeDate>
                      {new Date(prize.createdAt).toLocaleDateString()}
                    </PrizeDate>
                  </PrizeInfo>
                  <ButtonsContainer>
                    <PrizeStatus claimed={prize.claimed}>
                      <StatusIcon>{prize.claimed ? '✓' : '⌛'}</StatusIcon>
                      <StatusText>
                        {prize.claimed ? 'Reclamado' : 'Pendiente'}
                      </StatusText>
                    </PrizeStatus>
                    <InstructionsButton onClick={() => setSelectedPrize(prize)}>
                      <span>📋</span>
                      <span>Ver instrucciones</span>
                    </InstructionsButton>
                  </ButtonsContainer>
                </PrizeCard>
              ))
            ) : (
              <EmptyMessage>Aún no has ganado ningún premio</EmptyMessage>
            )}
          </List>
        </SectionContainer>

        <PromotionalSection>
          <PromotionalButton 
            href="https://tally.so/r/nGb6pe" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            
            ¿Quieres promocionar tu negocio?
          </PromotionalButton>
        </PromotionalSection>
      </MainContent>

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
      />

      <AnimatePresence>
        {selectedPrize && (
          <InstructionsOverlay onClick={() => setSelectedPrize(null)}>
            <InstructionsModal
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <InstructionsHeader>
                <InstructionsTitle>{selectedPrize.name}</InstructionsTitle>
                <CloseButton onClick={() => setSelectedPrize(null)}>&times;</CloseButton>
              </InstructionsHeader>
              
              <InstructionsContent>
                <CodeSection>
                  <Label>Código del premio:</Label>
                  <Code>{selectedPrize.code}</Code>
                </CodeSection>

                <InstructionsSection>
                  <Label>Instrucciones de canjeo:</Label>
                  <Instructions>
                    {selectedPrize.campaign?.redeemInstructions || 
                     'Contacta con el establecimiento para canjear tu premio.'}
                  </Instructions>
                </InstructionsSection>

                <WhatsAppButton onClick={() => handleWhatsAppShare(selectedPrize)}>
                  <WhatsAppIcon>📱</WhatsAppIcon>
                  <span>Enviarme por WhatsApp</span>
                </WhatsAppButton>
              </InstructionsContent>
            </InstructionsModal>
          </InstructionsOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url('/images/sort.jpeg') no-repeat center center fixed;
  background-size: cover;
  padding: 0;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
    z-index: -1;
  }
`;

const GradientTopBar = styled.header`
  position: relative;
  padding-top: 0.8rem;
  padding-bottom: 0.8rem;
  margin-bottom: 0.5rem;
`;

const HeaderTitle = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const MainTitle = styled.h1`
  font-family: 'Fredoka One', sans-serif;
  font-size: 2.1rem;
  font-weight: 700;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 3px white;
  margin: 0;
  margin-top: 1.8rem;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const SubTitle = styled.h2`
  font-family: 'Fredoka One', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #ffd700;
  letter-spacing: 1px;
  -webkit-text-stroke: 1px white;
  margin: 0;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    0 0 10px rgba(255, 215, 0, 0.5);
`;

const UserMenuContainer = styled.div`
  position: absolute;
  right: 1rem;
  top: 0.8rem;
  z-index: 9999;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 215, 0, 0.1);
  border: 2px solid rgba(255, 215, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffd700;
  font-size: 1.2rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(255, 215, 0, 0.2);
    border-color: rgba(255, 215, 0, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;

const MenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(88, 28, 135, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 0.5rem;
  min-width: 200px;
  z-index: 9999;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.8rem 1rem;
  background: none;
  border: none;
  border-radius: 8px;
  color: rgba(255, 215, 0, 0.8);
  display: flex;
  align-items: center;
  gap: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  text-align: left;

  &:hover {
    background: rgba(255, 215, 0, 0.1);
    color: #ffd700;
  }
`;

const MenuIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 215, 0, 0.1);
  margin: 0.5rem 0;
`;

const PrizeIcon = styled.div`
  font-size: 1.5rem;
  min-width: 40px;
  text-align: center;
`;

const PrizeInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const PrizeName = styled.div`
  color: #ffd700;
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
`;

const CampaignName = styled.div`
  color: rgba(255, 215, 0, 0.7);
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 0.2rem;
  &::before {
    content: '📍 ';
  }
`;

const PrizeCode = styled.div`
  color: #ffd700;
  font-size: 0.9rem;
  font-family: monospace;
  background: rgba(255, 215, 0, 0.1);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  display: inline-block;
  margin-top: 0.2rem;
  border: 1px solid rgba(255, 215, 0, 0.2);
  &::before {
    content: '🎫 ';
  }
`;

const PrizeDate = styled.div`
  color: rgba(255, 215, 0, 0.5);
  font-size: 0.8rem;
  margin-top: 0.2rem;
`;

const PrizeStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  background: ${props => props.claimed ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 215, 0, 0.2)'};
  color: ${props => props.claimed ? '#4ade80' : '#ffd700'};
  flex: 1;
  white-space: nowrap;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 215, 0, 0.7);
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 215, 0, 0.7);
`;

const MainContent = styled.main`
  flex: 1;
  padding: 1rem;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const SectionContainer = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: 'BaksoSapi', sans-serif;
  color: #ffd700;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  text-align: center;
  background: linear-gradient(to right, #ffd700, #ffb347);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CampaignCard = styled.div`
  background: rgba(88, 28, 135, 0.8);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  backdrop-filter: blur(10px);
  transition: transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
  }
`;

const CampaignInfo = styled.div`
  flex-grow: 1;
`;

const CampaignTitle = styled.div`
  color: #ffd700;
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 0.3rem;
`;

const PrizesCount = styled.div`
  color: rgba(255, 215, 0, 0.7);
  font-size: 0.9rem;
`;

const PlayButton = styled.div`
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.2);
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${CampaignCard}:hover & {
    background: rgba(255, 215, 0, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.1);
  }
`;

const PrizeCard = styled.div`
  background: rgba(88, 28, 135, 0.8);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  backdrop-filter: blur(10px);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
`;

const InstructionsButton = styled(ButtonBase)`
  flex: 1;
`;

const InstructionsOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const InstructionsModal = styled(motion.div)`
  position: relative;
  background: linear-gradient(135deg, 
    rgba(88, 28, 135, 0.95) 0%,
    rgba(67, 20, 102, 0.95) 100%
  );
  padding: 2rem;
  border-radius: 20px;
  width: 90%;
  max-width: 400px;
  border: 1px solid rgba(255, 215, 0, 0.2);
  box-shadow: 0 8px 32px rgba(76, 29, 149, 0.5);
  backdrop-filter: blur(10px);

  @media (max-width: 480px) {
    width: 85%;
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const InstructionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
  padding-bottom: 1rem;
`;

const InstructionsTitle = styled.h2`
  font-family: 'BaksoSapi', sans-serif;
  color: #ffd700;
  margin: 0;
  font-size: 1.5rem;
  letter-spacing: 1px;
`;

const InstructionsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0 1rem;
`;

const CodeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
`;

const Code = styled.div`
  font-family: monospace;
  font-size: 2rem;
  color: #ffd700;
  text-align: center;
  letter-spacing: 3px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const InstructionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 215, 0, 0.1);
`;

const Instructions = styled.div`
  color: #fff;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const Label = styled.div`
  color: rgba(255, 215, 0, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const WhatsAppButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: #25D366;
  color: white;
  border: none;
  padding: 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);

  &:hover {
    background: #128C7E;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const WhatsAppIcon = styled.span`
  font-size: 1.4rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 1.5rem;
  color: #ffd700;
`;

const StatusIcon = styled.span`
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffd700;
`;

const PromotionalSection = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  display: flex;
  justify-content: center;
`;

const PromotionalButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%);
  color: #ffd700;
  text-decoration: none;
  border: 2px solid rgba(255, 215, 0, 0.3);
  padding: 0.5rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  &:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(255, 215, 0, 0.2) 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StoreIcon = styled.span`
  font-size: 1.4rem;
`;

export default PrizesHistory; 